import type { FoxItSignature } from '@/FoxItTypes'
import type { PDFViewCtrl } from '@foxitsoftware/foxit-pdf-sdk-for-web-library'
import { captureException } from '@sentry/vue'

const storagePrefix = 'foxit-signatures:v1:'

const isSavedSignature = (
	value: Partial<FoxItSignature> | null
): value is Pick<FoxItSignature, 'dataURL'> =>
	typeof value === 'object' &&
	value !== null &&
	'dataURL' in value &&
	typeof value.dataURL === 'string' &&
	/^data:image\/[^;]+;base64,[A-Za-z0-9+/]+={0,2}$/.test(value.dataURL)

// Call once for a newly created viewer. Foxit's setter appends to its list.
export const persistFoxitSignatures = (
	pdfViewer: PDFViewCtrl.PDFViewer,
	sessionId: string | undefined
): (() => void) => {
	if (!sessionId) {
		return () => {}
	}

	// Capture the session belonging to this viewer, rather than looking up the
	// active session again when an event arrives.
	const storageKey = `${storagePrefix}${sessionId}`

	try {
		const stored = localStorage.getItem(storageKey)
		if (stored) {
			const signatures: unknown = JSON.parse(stored)
			if (Array.isArray(signatures)) {
				pdfViewer.setInkSignList(
					(signatures as Array<Partial<FoxItSignature> | null>).filter(
						isSavedSignature
					)
				)
			}
		}
	} catch {
		// Do not include stored signature image data in error reports.
		captureException(new Error('Unable to restore saved PDF signatures.'))
	}

	const save = () => {
		try {
			const signatures = pdfViewer.getInkSignList('base64')
			if (signatures.length) {
				localStorage.setItem(storageKey, JSON.stringify(signatures))
			} else {
				localStorage.removeItem(storageKey)
			}
		} catch {
			captureException(new Error('Unable to save PDF signatures.'))
		}
	}

	// Restore before listening: importing emits an added event for each entry.
	const emitter = pdfViewer.getEventEmitter()
	emitter.on('inkSign-added', save)
	emitter.on('inkSign-deleted', save)

	return () => {
		emitter.off('inkSign-added', save)
		emitter.off('inkSign-deleted', save)
	}
}

export const clearSavedFoxitSignatures = () => {
	try {
		for (let index = localStorage.length - 1; index >= 0; index--) {
			const key = localStorage.key(index)
			if (key?.startsWith(storagePrefix)) {
				localStorage.removeItem(key)
			}
		}
	} catch {
		captureException(new Error('Unable to clear saved PDF signatures.'))
	}
}
